import { ReactElement } from 'react';

import {
  CalendarDay,
  ChakraAvatar,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  Comment,
  EquipUIFireflyV2Theme,
  Globe,
  PinArea,
  Profile,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import useGetPatientStatus from '~/lib/hooks/useGetPatientStatus';
import {
  generateGenderText,
  getAgeFromDOB,
  getDiffInWeeks,
  getPatientCityStateAndTimezone,
} from '~/lib/util/patient.util';

import AdmissionDetail from './AdmissionDetail';
import PatientDetailItem from './PatientDetailItem';

const { secondary } = EquipUIFireflyV2Theme.colors;

const detailItemIconProps: {
  fill: string;
  height: string;
  width: string;
} = {
  fill: secondary[50],
  height: '20px',
  width: '20px',
};

type PatientDetailViewProps = {
  cards?: ReactElement[];
  id: string;
  patient: Patient;
  patientClinicalProfile: PatientClinicalProfile;
  patientPreferredFullName: string;
};

const AvatarFallBackIcon = ({
  patientFullName,
  fontSize = 'lg',
}: {
  patientFullName: string;
  fontSize?: 'lg' | 'sm';
}) => (
  <ChakraFlex alignItems="center" justifyContent="center">
    <ChakraText fontSize={fontSize}>
      {patientFullName
        .replace('"', '')
        .split(' ')
        .map((name: string) => name?.[0]?.toUpperCase() ?? '')
        .join('')}
    </ChakraText>
  </ChakraFlex>
);

const PatientDetailView = ({
  cards,
  id,
  patient,
  patientClinicalProfile,
  patientPreferredFullName,
}: PatientDetailViewProps) => {
  const {
    city,
    dateOfBirth,
    ethnicities,
    genderOffRecord,
    genderOnRecord,
    languages,
    pronounsOffRecord,
    pronounsOnRecord,
    state,
    timezoneValue,
  } = patient;

  const { status: patientStatus, style: patientStatusStyle } =
    useGetPatientStatus({ patient });

  const { programName, admitDate, closedDate } = patientClinicalProfile;
  const numWeeks = admitDate
    ? getDiffInWeeks(admitDate, closedDate, 'yyyy-LL-dd')
    : 0;

  return (
    <ChakraFlex
      flexDirection="column"
      id={`${id}-container`}
      justifyContent="space-between"
      minWidth="360px"
      padding="22px"
      width="360px"
    >
      <ChakraBox>
        <ChakraFlex alignItems="center" marginBottom="30px">
          <ChakraAvatar
            background="tertiary.marigold.background"
            color="primary.90.variant"
            height="44px"
            icon={
              <AvatarFallBackIcon
                fontSize="sm"
                patientFullName={patientPreferredFullName}
              />
            }
            id={`${id}-avatar`}
            overflow="hidden"
            width="44px"
          />
          <ChakraText
            id={`${id}-name`}
            marginLeft="12px"
            marginRight="16px"
            noOfLines={1}
            textTransform="capitalize"
          >
            {patientPreferredFullName}
          </ChakraText>
          <ChakraBox
            {...patientStatusStyle}
            borderRadius="100px"
            fontSize="10px"
            fontWeight="700"
            height="22px"
            id={`${id}-status`}
            lineHeight="22px"
            padding="0 8px"
          >
            {patientStatus}
          </ChakraBox>
        </ChakraFlex>
        <ChakraBox marginBottom="24px">
          <PatientDetailItem
            icon={<CalendarDay {...detailItemIconProps} />}
            id={`${id}-birthday`}
            text={
              dateOfBirth
                ? `${DateTime.fromSQL(dateOfBirth).toFormat(
                    'LL/dd/yyyy',
                  )} (${getAgeFromDOB(dateOfBirth, 'yyyy-LL-dd')} years old)`
                : 'unknown'
            }
          />
          {(genderOffRecord ||
            genderOnRecord ||
            pronounsOffRecord ||
            pronounsOnRecord) && (
            <PatientDetailItem
              icon={<Profile {...detailItemIconProps} />}
              id={`${id}-gender`}
              text={generateGenderText(
                genderOffRecord,
                genderOnRecord,
                pronounsOffRecord,
                pronounsOnRecord,
              )}
              whiteSpace="pre-line"
            />
          )}
          {Boolean((ethnicities ?? []).length) && (
            <PatientDetailItem
              icon={<Globe {...detailItemIconProps} />}
              id={`${id}-ethnicity`}
              text={ethnicities.join(', ')}
            />
          )}
          {Boolean((languages ?? []).length) && (
            <PatientDetailItem
              icon={<Comment {...detailItemIconProps} />}
              id={`${id}-languages`}
              text={languages.join(', ')}
            />
          )}
          <PatientDetailItem
            icon={<PinArea {...detailItemIconProps} />}
            id={`${id}-location`}
            text={`${getPatientCityStateAndTimezone(
              city,
              state,
              timezoneValue,
            )}`}
          />
        </ChakraBox>
        {cards}
      </ChakraBox>
      <AdmissionDetail
        id={`${id}-episode`}
        subTitle={
          admitDate
            ? `Admitted ${DateTime.fromSQL(admitDate).toFormat('LL/dd/yyyy')}${
                numWeeks > 0 ? ` (Week ${numWeeks})` : ''
              }`
            : ''
        }
        title={programName}
      />
    </ChakraFlex>
  );
};

export default PatientDetailView;
